import { useState, useEffect } from 'react';
import { Input, Select, Table, Button, LoadingSpinner } from '../../components/ui';
import { api } from '../../services/api-client';

interface Document {
  document_id: string;
  file_name: string;
  tag: string;
  tag_extra: string;
  uploaded_at: string;
  is_highlighted: boolean;
  mandatory_reading: boolean;
  area: string;
}

interface PaginatedResponse {
  data: Document[];
  pagination: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

const AREA_OPTIONS = [
  { value: 'PRIVATE', label: 'Area Privata' },
  { value: 'PUBLIC', label: 'Area Pubblica' },
  { value: 'TRAINING', label: 'Area Formazione' },
];

/**
 * Portale Documentale — pagina principale documenti.
 * Mostra i documenti visibili all'utente autenticato.
 */
export function DocumentsPage() {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [area, setArea] = useState('PRIVATE');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('area', area);
      params.set('page', String(page));
      params.set('pageSize', '25');
      if (search) params.set('search', search);

      const response = await api.get<PaginatedResponse>(`/portals/documentale/documents?${params.toString()}`);
      setDocuments(response.data.data);
      setTotalPages(response.data.pagination.totalPages);
      setTotalItems(response.data.pagination.totalItems);
    } catch {
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDocuments(); }, [area, page]);

  const handleSearch = () => {
    setPage(1);
    loadDocuments();
  };

  const handleDownload = (doc: Document) => {
    window.open(`/portals/documentale/documents/${doc.document_id}/download`, '_blank');
  };

  const columns = [
    {
      key: 'is_highlighted',
      header: '',
      width: '40px',
      render: (item: Document) => item.is_highlighted ? <span title="In evidenza">⭐</span> : null,
    },
    {
      key: 'tag',
      header: 'Tipo',
      render: (item: Document) => (
        <span className="doc-tag">{item.tag || 'Documento'}</span>
      ),
    },
    {
      key: 'tag_extra',
      header: 'Descrizione',
      render: (item: Document) => item.tag_extra || '-',
    },
    {
      key: 'file_name',
      header: 'File',
      render: (item: Document) => (
        <span className="doc-filename" title={item.file_name}>
          {item.file_name.length > 50 ? item.file_name.substring(0, 47) + '...' : item.file_name}
        </span>
      ),
    },
    {
      key: 'uploaded_at',
      header: 'Data',
      width: '120px',
      render: (item: Document) => item.uploaded_at ? new Date(item.uploaded_at).toLocaleDateString('it-IT') : '-',
    },
    {
      key: 'mandatory_reading',
      header: 'Obbl.',
      width: '60px',
      render: (item: Document) => item.mandatory_reading ? <span title="Lettura obbligatoria">📋</span> : null,
    },
    {
      key: 'actions',
      header: '',
      width: '120px',
      render: (item: Document) => (
        <Button variant="ghost" size="sm" onClick={() => handleDownload(item)}>
          📥 Scarica
        </Button>
      ),
    },
  ];

  return (
    <div className="documents-page">
      <div className="page-header">
        <h2>Documenti</h2>
        <span className="text-muted">{totalItems.toLocaleString()} documenti</span>
      </div>

      <div className="filters-bar">
        <Select
          label="Area"
          options={AREA_OPTIONS}
          value={area}
          onChange={(e) => { setArea(e.target.value); setPage(1); }}
        />
        <Input
          label="Cerca"
          placeholder="Tipo documento, descrizione..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
        />
        <Button variant="secondary" size="sm" onClick={handleSearch} style={{ alignSelf: 'flex-end', marginBottom: '1rem' }}>
          Cerca
        </Button>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <Table<Document>
            columns={columns}
            data={documents}
            keyField="document_id"
            loading={loading}
            emptyMessage="Nessun documento trovato in questa area."
          />

          {totalPages > 1 && (
            <div className="pagination" role="navigation" aria-label="Paginazione documenti">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
              >
                ← Precedente
              </Button>
              <span className="pagination-info">
                Pagina {page} di {totalPages}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
              >
                Successiva →
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
